import { Box, Stack, useTheme, dayjs } from "@carrotfertility/carotene-core"
import { FormattedMessage, useIntl } from "react-intl"
import React from "react"
import { RegistrationFlowLayout } from "./RegistrationLayouts"
import { ContinueButton, RegistrationButtonFooter, SkipButton } from "./RegistrationButtons"
import { useTrackView } from "#/utils/heap"
import { Form, FormDatePicker } from "@carrotfertility/carotene-core-x"
import { RegistrationFlowTitle } from "./RegistrationFlowTitle"
import { validateDueDate } from "#/services/common-forms"

interface DueDateStepProps {
  dueDate: string | null
  onBack: () => void
  onComplete: (formValues: { dueDate: string | null }) => void
  employeeId: number
}

export function DueDateStep({ dueDate, onBack, onComplete, employeeId }: DueDateStepProps): JSX.Element {
  useTrackView("DueDateStepRegFlow", "Due date step")
  const theme = useTheme()
  const intl = useIntl()

  const header = intl.formatMessage({
    defaultMessage: "What is your estimated due date?",
    description: "This relates to a pregnancy due date"
  })

  const onSubmit = (formValues: { dueDate?: dayjs.Dayjs }): void => {
    const formattedDate = formValues.dueDate ? formValues.dueDate.format("YYYY-MM-DD") : null
    onComplete({ dueDate: formattedDate })
  }

  const defaultValue = dueDate ? dayjs(dueDate) : null

  return (
    <>
      <RegistrationFlowTitle title={header} />
      <RegistrationFlowLayout
        header={header}
        description={intl.formatMessage({
          defaultMessage: "This information will help us guide you to the best care."
        })}
        interactiveElements={
          <Form onSubmit={onSubmit} defaultValues={{ dueDate: defaultValue }}>
            <Box marginTop={(theme) => theme.spacing(theme.tokens.spacing.lg)}>
              <Stack spacing={theme.spacing(theme.tokens.spacing.sm)}>
                <FormDatePicker
                  name="dueDate"
                  controllerProps={{
                    rules: {
                      validate: (value: dayjs.Dayjs | null | undefined) => {
                        // Only validate if a date is provided (skip is allowed)
                        if (!value) {
                          return true
                        }
                        const result = validateDueDate(value)
                        if (typeof result !== "boolean") {
                          return intl.formatMessage(result)
                        }
                        return result
                      }
                    }
                  }}
                />
              </Stack>
              <RegistrationButtonFooter
                onBack={onBack}
                skipButton={
                  <SkipButton onClick={() => onComplete({ dueDate: null })}>
                    <FormattedMessage defaultMessage="Skip" />
                  </SkipButton>
                }
                continueButton={
                  <ContinueButton>
                    <FormattedMessage defaultMessage="Continue" />
                  </ContinueButton>
                }
              />
            </Box>
          </Form>
        }
        employeeId={employeeId}
      />
    </>
  )
}
